import React, {useState, useEffect, useContext, useMemo} from "react";
import DashboardWidget from "./DashboardWidget";
import axios from "@common/axios";
import {BrandContext} from "@context/BrandContext";
import {UserContext} from "@context/UserContext";
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import ListItemTodo from "@common/ListItemTodo";
import ButtonsGroup from "@common/ButtonsGroup";
import Button from "@common/Button";
import PopConfirm from "@common/PopConfirm";
import TableAntD from "@common/TableAntD";
import createTableAntDHeaderFilter from "@common/TableAntDHeaderFilterColumn";

const StudentToDoListDashboardWidget = props => {
    const {...other} = props;
    const todoTypes = {
        TEST: {type: 'TEST', label: 'Tests'},
        FOLLOWUP_WORK: {type: 'FOLLOWUP_WORK', label: 'Follow-up Work'},
        TRAIL: {type: 'TRAIL', label: 'Tasks'},
        COMPETITION: {type: 'COMPETITION', label: 'Competitions'}
    };
    const [isLoading, setLoading] = useState(false);
    const [todoType, setTodoType] = useState(todoTypes.TEST);
    const [todoList, setTodoList] = useState([]);
    const brandConfig = useContext(BrandContext);
    const {loadShortcutByUrl} = useCurrentShortcut();
    const {user} = useContext(UserContext);

    useEffect(() => {
        setLoading(true);
        if (todoType !== undefined) {
            axios.post('/web/ui/todo/userToDoListType', [todoType.type], {})
                .then(response => {
                    setTodoList(response.data);
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [todoType]);

    const handleClick = (todo) => {
        if(todo === undefined) {
            return;
        }

        if (todo.toDoType === todoTypes.TEST.type) {
            if(todo.url === '/controlCentre/startAdaptiveTest.action') {
                loadShortcutByUrl(todo.url, {testAssignmentId: todo.testAssignmentId});
            } else {
                loadShortcutByUrl(todo.url, undefined, todo.testAssignmentId);
            }
        } else if (todo.toDoType === todoTypes.FOLLOWUP_WORK.type) {
            loadShortcutByUrl(todo.url, {testAssignmentId: todo.testAssignmentId}, todo.testResultId);
        } else if (todo.toDoType === todoTypes.TRAIL.type) {
            if (todo.url === '/trail/loadTrail.action') {
                let params = {currentPathwayAssignmentId: todo.trailAssignmentId};
                if (todo.conceptId != null) params.conceptId = todo.conceptId;
                loadShortcutByUrl(todo.url, params);
            } else {
                loadShortcutByUrl(todo.url, undefined, todo.trailAssignmentId);
            }
        } else if (todo.toDoType === todoTypes.COMPETITION.type) {
            loadShortcutByUrl(todo.url, {competitionId: todo.competitionId, startCompetitionFromDashboard: true});
        }
    };

    const toDoListCols = useMemo(() => {
        const nameColumn = {
            title: (() => {
                if (todoType.type === todoTypes.TEST.type) return `${brandConfig.testName} name`;
                if (todoType.type === todoTypes.FOLLOWUP_WORK.type) return brandConfig.diagnosticFeedbackName;
                if (todoType.type === todoTypes.TRAIL.type) return `${brandConfig.taskName} name`;
                if (todoType.type === todoTypes.COMPETITION.type) return brandConfig.competitionName;
                return 'Name';
            })(),
            key: 'title',
            dataIndex: 'title',
            width: '80%',
        };
        const conditionalCols = todoType.type === todoTypes.FOLLOWUP_WORK.type
            ? [
                { title: 'Due', dataIndex: 'dueDate', key: 'dueDate',
                    render:(val)=>{
                        return user.dateFormat.format(val)
                    }
                },
                { title: 'Skills', dataIndex: 'skillsCount', key: 'skillsCount' }
            ]
            : [
                { title: 'Assigned', dataIndex: 'startDate', key: 'startDate', width: 50,
                render:(val)=>{
                    return user.dateFormat.format(val)
                }},
                { title: 'Expires', dataIndex: 'endDate', key: 'endDate',
                    render:(val)=>{
                        return user.dateFormat.format(val)
                }}
        ];

        return [
            nameColumn,
            ...conditionalCols,
            { title: 'Action', key: 'action', dataIndex: 'action',
            render:(val, record)=>{
                return (
                    (todoType.type === todoTypes.TEST.type && record.testType === 'ASSESSMENT')?
                        <PopConfirm
                        title={<>You only get one attempt to answer all questions.<br/>Are you sure you want to start this {brandConfig.testName}?</>}
                        okText={'Start ' + brandConfig.testName}
                        cancelText="Cancel"
                        onConfirm={()=>{handleClick(record)}}
                    >
                    <Button type={'text'}>Start</Button>
                </PopConfirm> :
                <Button type={'text'} onClick={()=>{handleClick(record)}}>
                    {todoType.type === 'FOLLOWUP_WORK' ? <>View Feedback</> : <>Start</>}
                </Button>
                )
            }}
        ];

    }, [todoType, todoTypes]);

    return (
        <>
            {brandConfig.use2025Dashboard &&
                <h2>My to do list</h2>
            }
            <DashboardWidget
                title={
                    !brandConfig.use2025Dashboard &&
                    <div className="ch-dashboard-title-with-icon">
                        <svg className="ch-icon-medium" viewBox="0 0 128 128">
                            <use href="#tests-icon"></use>
                        </svg>
                        <span>My to do list</span>
                    </div>
                }
                isLoading={isLoading}
                {...other}>
                <ButtonsGroup className="margin-b--l">
                    <Button type={(todoType.type === todoTypes.TEST.type ? '' : 'outline')} onClick={()=>{setTodoType(todoTypes.TEST)}} >{brandConfig.testName}</Button>
                    {brandConfig.hasDiagnosticTests &&
                        <Button type={(todoType.type === todoTypes.FOLLOWUP_WORK.type ? '' : 'outline')} onClick={()=>{setTodoType(todoTypes.FOLLOWUP_WORK)}}>{brandConfig.diagnosticFeedbackName}</Button>
                    }
                    <Button type={(todoType.type === todoTypes.TRAIL.type ? '' : 'outline')} onClick={()=>{setTodoType(todoTypes.TRAIL)}} >{brandConfig.taskName}</Button>
                    {brandConfig.hasScorchers &&
                        <Button type={(todoType.type === todoTypes.COMPETITION.type ? '' : 'outline')} onClick={()=>{setTodoType(todoTypes.COMPETITION)}} >{brandConfig.competitionName}</Button>
                    }
                </ButtonsGroup>
                <div className={'ch-dashboard-blk-body'}>
                    {/*{todoList.length > 0 &&*/}
                        <TableAntD
                            rowKey="testAssignmentId"
                            className={'ch-dashboard-widget-table ch-table'}
                            columnData={toDoListCols}
                            data={todoList}
                            noDataMessage={<div className="ch-list-item-disabled ch-warning-text">No {todoType.label} assigned</div>}
                        />
                </div>
            </DashboardWidget>
        </>
    )
}

export default StudentToDoListDashboardWidget;